// src/components/Performance/AddNoteForm.tsx

import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Button, DatePicker, Select, Input } from "antd";
import dayjs from "dayjs";
import "./AddNoteForm.scss";
import FloatLabel from "../../../../../../components/FloatLabel";
import { useGetApiRequests, triggerNotification } from "../../../../../../common/CommonExport";
import { mapApiTypeToFormType, mapFormTypeToApiType, noteTypes } from "./performanceHelper";
import { performanceNoteSchema } from "./validationSchemas";

interface AddNoteFormProps {
  onSave?: (values: any) => void;
  onCancel: () => void;
  employeeId: number;
  refreshData?: () => void;
  isEdit?: boolean;
  noteData: any;
}

const AddNoteForm: React.FC<AddNoteFormProps> = ({ onSave, onCancel, employeeId, refreshData, isEdit = false, noteData }) => {
  const addPerformanceNote = useGetApiRequests("addPerformanceNote", "POST");
  const updatePerformanceNote = useGetApiRequests("updatePerformanceNote", "PUT");

  const [loadingButton, setLoadingButton] = useState<"draft" | "submit" | null>(null);

  const initialValues = isEdit
    ? {
      date: noteData.date || dayjs().format("YYYY-MM-DD"),
      noteType: mapApiTypeToFormType(noteData.type),
      title: noteData.title || "",
      details: noteData.description || "",
      nextSteps: noteData.nextSteps || "",
    }
    : {
      date: dayjs().format("YYYY-MM-DD"),
      noteType: "",
      title: "",
      details: "",
      nextSteps: "",
    };

  const saveNote = async (values: any, reviewStatus: "Draft" | "Saved") => {
    const payload: any = {
      employeeId,
      title: values.title,
      description: values.details,
      type: mapFormTypeToApiType(values.noteType),
      nextSteps: values.nextSteps,
      reviewStatus,
    };

    if (isEdit && noteData?.employeePerformanceNotesId) {
      payload.employeePerformanceNotesId = noteData.employeePerformanceNotesId;
    }

    try {
      const apiCall = isEdit ? updatePerformanceNote : addPerformanceNote;
      const res = await apiCall(payload, {}, { employeeId });

      if ((res as any)?.data?.status?.success === "Success") {
        let successMessage = "";
        if (isEdit && reviewStatus === "Draft") successMessage = "Draft updated successfully.";
        else if (isEdit) successMessage = "Note updated successfully.";
        else if (reviewStatus === "Draft") successMessage = "Draft saved successfully.";
        else successMessage = "Note added successfully.";

        triggerNotification("success", successMessage, "", "topRight", 3);

        if (refreshData) await refreshData();
        onCancel();
      } else {
        triggerNotification("error", (res as any)?.data?.status?.message || "Failed to save note.", "", "topRight", 3);
      }
    } catch (error: any) {
      console.error("Error saving note:", error);
      triggerNotification("error", "Failed to save note.", "", "topRight", 3);
    }
  };

  return (
    <div className="add-note-container">
      <Formik
        initialValues={initialValues}
        validationSchema={performanceNoteSchema}
        onSubmit={async (values) => {
          setLoadingButton("submit");
          try {
            await saveNote(values, "Saved");
          } finally {
            setLoadingButton(null);
          }
        }}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {({ setFieldValue, values, setTouched }) => (
          <Form>
            <div className="flex flex-row gap-4 w-full">
              <div className="form-field w-full mb-4">
                <FloatLabel label="Date of Note*" value={values.date}>
                  <DatePicker
                    className="float-control h-[40px]"
                    format="DD/MM/YYYY"
                    value={dayjs(values.date)}
                    onChange={(date) => setFieldValue("date", date?.format("YYYY-MM-DD"))}
                    disabled={loadingButton !== null}
                    disabledDate={(current) => current && current > dayjs().endOf("day")}
                  />
                </FloatLabel>
                <ErrorMessage name="date" component="div" className="error-message" />
              </div>

              <div className="form-field w-full mb-4">
                <FloatLabel label="Note Type*" value={values.noteType}>
                  <Select className="float-control h-[40px]" value={values.noteType} onChange={(value) => setFieldValue("noteType", value)} disabled={loadingButton !== null}>
                    {noteTypes.map((item) => (
                      <Select.Option key={item.value} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FloatLabel>
                <ErrorMessage name="noteType" component="div" className="error-message" />
              </div>
            </div>

            <div className="form-field mb-4">
              <FloatLabel label="Title/Subject*" value={values.title}>
                <Field name="title">{({ field }: any) => <Input {...field} className="float-control h-[40px]" placeholder="" disabled={loadingButton !== null} />}</Field>
              </FloatLabel>
              <ErrorMessage name="title" component="div" className="error-message" />
            </div>

            <div className="form-field mb-4">
              <FloatLabel label="Details of Note*" value={values.details}>
                <Field name="details">{({ field }: any) => <Input.TextArea {...field} className="float-control" rows={5} placeholder="" disabled={loadingButton !== null} />}</Field>
              </FloatLabel>
              <ErrorMessage name="details" component="div" className="error-message" />
            </div>

            <div className="form-field mb-4">
              <FloatLabel label="Next Steps/Action Items (Optional)" value={values.nextSteps}>
                <Field name="nextSteps">{({ field }: any) => <Input.TextArea {...field} className="float-control" rows={3} placeholder="" disabled={loadingButton !== null} />}</Field>
              </FloatLabel>
            </div>

            <div className="button-group flex justify-end gap-4">
              <Button
                type="default"
                className="cancelButton"
                disabled={loadingButton !== null}
                loading={loadingButton === "draft"}
                onClick={async () => {
                  setTouched({});
                  setLoadingButton("draft");
                  try {
                    await saveNote(values, "Draft");
                  } finally {
                    setLoadingButton(null);
                  }
                }}
              >
                Save Draft
              </Button>

              <Button type="primary" htmlType="submit" className="addButton" disabled={loadingButton !== null} loading={loadingButton === "submit"}>
                {isEdit ? "Update Note" : "Add Note"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddNoteForm;
